/**
 * Response schemas for the blueprint admin API
 * (src/api/v4/admin-blueprints.ts), mounted at `/api/v4/admin/blueprints`
 * (wizard UI, session) and `/api/v4/agent/admin/blueprints` (agents, API key).
 *
 * Each schema lists every key the route sent before it went through
 * `sendJson`; unlisted keys are stripped. `sendJson` parses BEFORE JSON
 * serialization, so timestamps accept a Date (Prisma) or an ISO string.
 *
 * Json columns (`actions`, `card`, `detail`) use `PrismaJsonColumnSchema`,
 * not an object-only record: the columns hold arrays and legacy shapes, and a
 * strict shape here would turn one drifted row into a 500 on the list route.
 */
import { z } from 'zod';
import { DateOrIsoStringSchema } from '../schemas/date-or-iso';
import { PrismaJsonColumnSchema } from '../schemas/json-value';
import { BlueprintSampleRowSchema } from '../view-system/blueprint-sample-row.schema';
import { BlueprintSourceSchema } from '../view-system/blueprint-source.schema';
import { BlueprintTestResponseSchema } from '../view-system/card-slot-test.schema';
import { ViewItemSchema } from '../view-system/view-item.schema';

const BlueprintValidationIssueWireSchema = z.object({
  field: z.string(),
  value: z.string(),
  message: z.string(),
  code: z.literal('missing_required_config_field').optional(),
});

/** `toBlueprintResponse` output. `status` etc. stay plain strings: the columns carry pre-registry legacy ids. */
export const BlueprintAdminResponseSchema = z.object({
  id: z.number(),
  kind: z.string(),
  displayName: z.string(),
  intent: z.string(),
  artifactCard: z.string(),
  defaultView: z.string(),
  actions: PrismaJsonColumnSchema,
  card: PrismaJsonColumnSchema,
  detail: PrismaJsonColumnSchema,
  bundled: z.boolean(),
  autoArchive: z.boolean(),
  showDealStage: z.boolean(),
  sampleRow: BlueprintSampleRowSchema.nullable(),
  isSignal: z.boolean(),
  groupPreset: z.string().nullable(),
  clayTableUrl: z.string().nullable(),
  source: BlueprintSourceSchema.nullable(),
  status: z.string(),
  createdAt: DateOrIsoStringSchema,
  updatedAt: DateOrIsoStringSchema,
});

/** create / update: the blueprint plus the non-blocking publish-readiness report. */
export const BlueprintWithIssuesResponseSchema = BlueprintAdminResponseSchema.extend({
  publishIssues: z.array(BlueprintValidationIssueWireSchema),
});

/** publish: the blueprint plus the optional terminal-coverage warnings. */
export const BlueprintPublishResponseSchema = BlueprintAdminResponseSchema.extend({
  publishWarnings: z.array(z.string()).optional(),
});

export const BlueprintListResponseSchema = z.array(BlueprintAdminResponseSchema);

export const BlueprintExistsResponseSchema = z.object({ exists: z.boolean() });

/** `/:kind/test` — the shared strict schema is what the route emits. */
export const BlueprintTestAdminResponseSchema = BlueprintTestResponseSchema;

export const ImpactPreviewItemSchema = z.object({
  original: ViewItemSchema,
  modified: ViewItemSchema,
});
export const ImpactPreviewResponseSchema = z.object({
  items: z.array(ImpactPreviewItemSchema),
  totalCount: z.number(),
});

export const MappingProposalAmbiguousEntrySchema = z.object({
  key: z.string(),
  candidates: z.array(z.string()),
  reason: z.literal('ambiguous'),
});
export const MappingProposalResponseSchema = z.object({
  mapped: z.record(z.string(), z.string()),
  ambiguous: z.array(MappingProposalAmbiguousEntrySchema),
  unmapped: z.array(z.string()),
});

const ConfigFieldSummaryWireSchema = z.object({
  required: z.record(z.string(), z.string()),
  optional: z.record(z.string(), z.string()),
});

/** `GET /action-types`. */
export const ActionTypeDiscoveryResponseSchema = z.object({
  handlers: z.record(z.string(), ConfigFieldSummaryWireSchema),
  actionTypes: z.array(
    z.object({
      slug: z.string(),
      label: z.string(),
      defaultInteraction: z.string(),
      suggestedHandler: z.string().nullable(),
      interactionConfig: ConfigFieldSummaryWireSchema,
      exampleBinding: z.object({
        actionTypeSlug: z.string(),
        handler: z.string(),
        handlerConfig: z.record(z.string(), PrismaJsonColumnSchema),
        interactionConfig: z.record(z.string(), PrismaJsonColumnSchema),
      }),
    }),
  ),
});

/** `POST /:kind/cue` (202). */
export const StudioCueAckResponseSchema = z.object({ ok: z.literal(true) });

/** `POST /:kind/references` (201). */
export const BlueprintReferenceResponseSchema = z.object({ seq: z.number() });

// Staging-only reset / seed / replay routes

export const BlueprintResetResponseSchema = z.object({
  ok: z.literal(true),
  deleted: z.object({ artifacts: z.number(), blueprints: z.number() }),
});
export const BlueprintResetAllResponseSchema = z.object({
  ok: z.literal(true),
  deleted: z.object({ artifacts: z.number(), blueprints: z.number(), executions: z.number() }),
});
export const BlueprintResetNuclearResponseSchema = z.object({
  ok: z.literal(true),
  deleted: z.object({
    executions: z.number(),
    artifacts: z.number(),
    blueprints: z.number(),
    legacy: z.number(),
  }),
});

export const SeedStatusResponseSchema = z.object({
  status: z.object({
    blueprints: z.boolean(),
    fill: z.object({ seeded: z.number(), total: z.number() }),
    legacy: z.boolean(),
  }),
});

export const SeedLegacyResponseSchema = z.object({
  ok: z.literal(true),
  results: z.array(z.object({ kind: z.string(), action: z.enum(['created', 'exists']) })),
});

/** `ok` + `deleted: <count>` — legacy removal, concept removal, fill removal. */
export const SeedDeletedCountResponseSchema = z.object({ ok: z.literal(true), deleted: z.number() });

const SeedPerUserSchema = z.object({
  userId: z.number(),
  email: z.string().nullable(),
  result: z.object({ blueprints: z.number().optional(), artifacts: z.number() }),
});

export const SeedConceptsResponseSchema = z.object({
  ok: z.literal(true),
  summary: z.object({ blueprints: z.number(), artifacts: z.number() }),
  perUser: z.array(SeedPerUserSchema).optional(),
});
export const SeedBlueprintsResponseSchema = z.object({
  ok: z.literal(true),
  summary: z.object({ blueprints: z.number() }),
});
export const SeedFillResponseSchema = z.object({
  ok: z.literal(true),
  summary: z.object({ artifacts: z.number() }),
});

export const StoryArcsListResponseSchema = z.object({
  arcs: z.array(
    z.object({
      id: z.string(),
      displayName: z.string(),
      description: z.string(),
      accountName: z.string(),
      theme: z.string(),
      beatCount: z.number(),
      blueprintKinds: z.array(z.string()),
      active: z.boolean(),
      seeded: z.number(),
      total: z.number(),
    }),
  ),
});
export const StoryArcAddedResponseSchema = z.object({
  ok: z.literal(true),
  arcId: z.string(),
  artifacts: z.number(),
});
export const StoryArcRemovedResponseSchema = z.object({
  ok: z.literal(true),
  arcId: z.string(),
  deleted: z.number(),
});

export const ReplayResponseSchema = z.object({
  ok: z.literal(true),
  message: z.string().optional(),
  summary: z.object({
    totalRows: z.number(),
    artifactTypes: z.number(),
    created: z.number(),
    updated: z.number().optional(),
    skipped: z.number().optional(),
    errors: z.number(),
  }),
  results: z.array(
    z.object({
      replayExternalId: z.string(),
      artifactType: z.string().nullable(),
      status: z.enum(['created', 'error']),
      httpStatus: z.number().optional(),
      error: z.string().optional(),
    }),
  ),
});

// GET /diagnostics/slot-resolution

const SlotResolutionSampleSchema = z.object({
  uuid: z.string(),
  data: z.record(z.string(), PrismaJsonColumnSchema),
  hasSfLink: z.boolean(),
  sfAccountName: z.string().nullable(),
});

export const SlotResolutionDiagnosticResponseSchema = z.object({
  summary: z.object({
    totalBlueprints: z.number(),
    totalArtifacts: z.number(),
    patternsFound: z.number(),
    healthyBlueprints: z.number(),
  }),
  patterns: z.array(
    z.object({
      dataKey: z.string(),
      severity: z.enum(['critical', 'warning']),
      affectedBlueprints: z.number(),
      affectedArtifacts: z.number(),
      slots: z.array(z.string()),
      diagnosis: z.object({
        type: z.enum(['field_never_sent', 'possible_typo', 'sf_link_missing', 'null_values', 'sf_name_null', 'mixed']),
        detail: z.string(),
        similarKeys: z.array(z.string()).optional(),
        sfLinkRate: z.number().optional(),
      }),
      suggestedFix: z.string(),
      resolutionChain: z.array(
        z.object({ source: z.string(), status: z.enum(['hit', 'miss', 'partial', 'no_link']) }),
      ),
      blueprints: z.array(
        z.object({
          kind: z.string(),
          displayName: z.string(),
          artifactCount: z.number(),
          sampledArtifacts: z.number(),
          hitRate: z.number(),
          sfLinkCount: z.number(),
          availableFields: z.array(z.string()),
          sampleArtifacts: z.array(SlotResolutionSampleSchema),
        }),
      ),
    }),
  ),
  healthy: z.array(
    z.object({
      kind: z.string(),
      displayName: z.string(),
      artifactCount: z.number(),
      slotCount: z.number(),
    }),
  ),
});
